/**
 * Back from Stripe, after buying a pack.
 *
 * THE CREDIT IS THE WEBHOOK'S JOB, NOT THIS PAGE'S. Stripe redirects the
 * customer here the moment the card clears, which is routinely before the
 * `checkout.session.completed` webhook has reached our backend and credited
 * the units. So this page does not assert a balance it has not seen: it
 * confirms the payment, then re-reads the balance a few times and shows the
 * new number when it appears.
 *
 * The one thing it must never do is say "0 left" while the credit is in
 * flight. A customer who has just paid and is told they have nothing will ask
 * for their money back, and they will be right to.
 */

import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useHistory, useLocation } from 'react-router-dom';
import { PiCheckCircleLight, PiXCircleLight } from 'react-icons/pi';
import { useAppDispatch } from '../../../store';
import { UnitBalance, fetchUnitBalances } from '../../../store/actions/PackActions';
import GlassCard from '../brand/GlassCard';
import Button from '../TailwindComponents/Button';
import Spinner from '../TailwindComponents/Spinner';

/** How long to wait for the webhook before saying "it will appear shortly". */
const CREDIT_POLL_MS = 2000;
const CREDIT_ATTEMPTS = 6;

const PackPurchaseComplete: React.FC = () => {
  const { t } = useTranslation();
  const dispatch = useAppDispatch();
  const history = useHistory();
  const location = useLocation();

  const params = new URLSearchParams(location.search);
  const cancelled = params.get('status') === 'cancelled';

  const [balances, setBalances] = useState<UnitBalance[] | null>(null);
  const [settled, setSettled] = useState(false);
  const attemptsRef = useRef(0);

  const poll = useCallback(async (): Promise<boolean> => {
    const summary: any = await dispatch(fetchUnitBalances() as any);
    const rows: UnitBalance[] = summary?.balances || [];
    if (!rows.length) return false;
    setBalances(rows);
    // Any prepaid balance at all is evidence the credit has landed. Before the
    // webhook the pack rows are absent or zero across the board.
    return rows.some((row) => row.pack_balance > 0);
  }, [dispatch]);

  useEffect(() => {
    if (cancelled) { setSettled(true); return; }
    let stopped = false;
    const tick = async () => {
      if (stopped) return;
      const credited = await poll();
      attemptsRef.current += 1;
      if (credited || attemptsRef.current >= CREDIT_ATTEMPTS) {
        setSettled(true);
        return;
      }
      setTimeout(tick, CREDIT_POLL_MS);
    };
    tick();
    return () => { stopped = true; };
  }, [cancelled, poll]);

  if (cancelled) {
    return (
      <div className="h-full flex items-center justify-center bg-bg px-6">
        <GlassCard className="max-w-md w-full">
          <div className="flex flex-col items-center text-center gap-3 py-2">
            <PiXCircleLight className="w-8 h-8 text-fg-muted" aria-hidden />
            <p className="text-sm font-medium text-fg-strong">{t('Purchase cancelled')}</p>
            <p className="text-xs text-fg-muted leading-relaxed">
              {t('Nothing was charged. Your balance is unchanged.')}
            </p>
            <Button variant="primary" size="small" className="mt-1"
                    onClick={() => history.push('/packs')}>
              {t('Back to packs')}
            </Button>
          </div>
        </GlassCard>
      </div>
    );
  }

  const withUnits = (balances || []).filter((row) => row.pack_balance > 0);

  return (
    <div className="h-full flex items-center justify-center bg-bg px-6">
      <GlassCard className="max-w-md w-full" data-testid="pack-complete">
        <div className="flex flex-col items-center text-center gap-3 py-2">
          <PiCheckCircleLight className="w-8 h-8 text-success-500" aria-hidden />
          <p className="text-sm font-medium text-fg-strong">{t('Payment received')}</p>

          {!settled ? (
            <div className="flex flex-col items-center gap-2">
              <Spinner size="sm" />
              <p className="text-xs text-fg-muted">{t('Crediting your balance…')}</p>
            </div>
          ) : withUnits.length ? (
            <div className="w-full space-y-1.5 pt-1" data-testid="pack-credited">
              <p className="text-xs text-fg-muted">{t('Your balance now:')}</p>
              {withUnits.map((row) => (
                <div key={row.unit_type}
                     className="flex items-center justify-between gap-3 px-3 py-2 rounded-lg
                                bg-surface-sunken/70 border border-hairline-soft">
                  <span className="text-xs text-fg-body">{t(row.label)}</span>
                  <span className="text-sm font-medium text-fg-strong tabular-nums">
                    {row.pack_balance.toLocaleString()}
                  </span>
                </div>
              ))}
            </div>
          ) : (
            /* Paid, but the credit has not shown up in the time we waited.
               It is coming; saying so is honest and saying "0" would not be. */
            <p className="text-xs text-fg-muted leading-relaxed" data-testid="pack-credit-pending">
              {t('Your units will appear in your balance shortly. You can close this page.')}
            </p>
          )}

          <div className="flex items-center gap-2 pt-1">
            <Button variant="outline" size="small" onClick={() => history.push('/packs')}>
              {t('Buy another')}
            </Button>
            <Button variant="primary" size="small" onClick={() => history.push('/')}>
              {t('Done')}
            </Button>
          </div>
        </div>
      </GlassCard>
    </div>
  );
};

export default PackPurchaseComplete;
